import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  scheduledAlertForDisplay,
  scheduledAlertInput,
} from "@/lib/alerts/display-time";
import { alerts as api } from "@/lib/api/alerts";
import type { AlertInput } from "@/lib/api/alerts";
import type { ErrorCode } from "@/lib/error-catalog";
import {
  useUserChannel,
  type UserChannelMessage,
} from "@/hooks/useUserChannel";
import type { AlertData } from "@/types/alert";
import type { Alert } from "@/types/api";
import { useAuth } from "./useAuth";
import { useDisplayTimeZone } from "./useDisplayTimeZone";

// `re-arm semantics`: editing `condition` server-side flips active=true and
// clears triggered_at. The hook just reflects whatever the server returns —
// it doesn't try to anticipate the flip locally so optimistic updates can't
// drift from the server's view.
//
// `triggered`: Set<string> of alert ids known to have fired, mirrored
// from the cable stream. Seeded on mount from /alert_deliveries (so an
// already-fired alert shows as triggered immediately on page load) and
// appended whenever Alerts::Evaluator broadcasts `alert.delivered` over
// UserChannel. Matches the legacy abg_one shape — `triggered_at` on the
// row alone is insufficient because non-once recurrences never set it,
// yet the user still wants the bell badge to pop on each fire.

export type AlertsStatus = "idle" | "loading" | "ready" | "error";

type AlertDelivery = { push: boolean; email: boolean };
type PriceRecurrence = "once" | "daily";
type ScheduledRecurrence = "once" | "daily" | "weekly" | "monthly";

export interface AlertsContextValue {
  status: AlertsStatus;
  alerts: AlertData[];
  triggered: ReadonlySet<string>;
  triggeredAlerts: AlertData[];
  error: ErrorCode | null;
  reload: () => Promise<void>;
  clearError: () => void;
  reportError: (e: unknown) => void;
  // Legacy contract — kept stable so AlertsView consumes one shape.
  addAlert: (
    metric: "spx" | "vix",
    operator: "gt" | "lt",
    value: number,
    delivery: AlertDelivery,
    title: string,
    body: string,
    recurrence: PriceRecurrence,
  ) => Promise<boolean>;
  addScheduledAlert: (
    date: string,
    title: string,
    body: string,
    delivery: AlertDelivery,
    recurrence: ScheduledRecurrence,
    time: string,
  ) => Promise<boolean>;
  editAlert: (
    id: string,
    metric: "spx" | "vix",
    operator: "gt" | "lt",
    value: number,
    delivery: AlertDelivery,
    title: string,
    body: string,
    recurrence: PriceRecurrence,
  ) => Promise<boolean>;
  editScheduledAlert: (
    id: string,
    date: string,
    title: string,
    body: string,
    delivery: AlertDelivery,
    recurrence: ScheduledRecurrence,
    time: string,
  ) => Promise<boolean>;
  deleteAlert: (id: string) => Promise<void>;
  toggleAlert: (id: string, active: boolean) => Promise<void>;
  reorderAlerts: (orderedIds: string[]) => Promise<void>;
  getStatus: (a: AlertData) => "active" | "paused";
  // Generic CRUD — used by lower-level callers / tests.
  create: (input: AlertInput) => Promise<Alert>;
  update: (id: string, patch: AlertInput) => Promise<Alert>;
  remove: (id: string) => Promise<void>;
}

const AlertsContext = createContext<AlertsContextValue>(null!);

export function useAlerts(): AlertsContextValue {
  return useContext(AlertsContext);
}

function classifyError(e: unknown): ErrorCode {
  const msg = e instanceof Error ? e.message.toLowerCase() : "";
  if (msg.includes("rate") || msg.includes("429")) return "rate_limited";
  if (msg.includes("unauthorized") || msg.includes("401"))
    return "unauthorized";
  if (msg.includes("forbidden") || msg.includes("403")) return "forbidden";
  if (msg.includes("not found") || msg.includes("404")) return "not_found";
  if (msg.includes("network") || msg.includes("fetch")) return "network";
  return "unknown";
}

function asAlertData(a: Alert): AlertData {
  return a as unknown as AlertData;
}

function useAlertsProvider(): AlertsContextValue {
  const { status: authStatus } = useAuth();
  const timeZone = useDisplayTimeZone();
  const [status, setStatus] = useState<AlertsStatus>("idle");
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [triggered, setTriggered] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<ErrorCode | null>(null);
  const channel = useUserChannel();

  const reload = useCallback(
    () =>
      api
        .listAlerts()
        .then(({ alerts }) => {
          setAlerts([...alerts]);
          setStatus("ready");
          setError(null);
        })
        .catch((e: unknown) => {
          setError(classifyError(e));
          setStatus("error");
        }),
    [],
  );

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    void reload();
  }, [authStatus, reload]);

  // Seed the triggered set from persisted delivery rows on mount so an
  // already-fired alert shows as triggered without waiting for the next
  // cable broadcast. Failures are non-fatal — the cable will still
  // populate the set as new alerts fire.
  useEffect(() => {
    if (authStatus !== "authenticated") return;
    let cancelled = false;
    api
      .listAlertDeliveries()
      .then(({ alert_deliveries }) => {
        if (cancelled) return;
        setTriggered(new Set(alert_deliveries.map((d) => d.alert_id)));
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, [authStatus]);

  // Live updates: Alerts::Evaluator on the server broadcasts
  // { event: "alert.delivered", alert_id, ... } to UserChannel after
  // each successful deliver_batch. We append the id to the set; the
  // memoized triggeredAlerts derivation surfaces the row.
  useEffect(() => {
    if (!channel) return;
    return channel.subscribe((msg: UserChannelMessage) => {
      if (msg.event !== "alert.delivered") return;
      const id = msg.alert_id;
      if (typeof id !== "string") return;
      setTriggered((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    });
  }, [channel]);

  const create = useCallback(
    async (input: AlertInput): Promise<Alert> => {
      const { alert } = await api.createAlert(
        scheduledAlertInput(input, timeZone),
      );
      setAlerts((prev) => [alert, ...prev]);
      return scheduledAlertForDisplay(alert, timeZone);
    },
    [timeZone],
  );

  const update = useCallback(
    async (id: string, patch: AlertInput): Promise<Alert> => {
      const { alert } = await api.updateAlert(
        id,
        scheduledAlertInput(
          patch,
          timeZone,
          alerts.find((item) => item.id === id)?.type,
        ),
      );
      setAlerts((prev) => prev.map((a) => (a.id === id ? alert : a)));
      return scheduledAlertForDisplay(alert, timeZone);
    },
    [alerts, timeZone],
  );

  const remove = useCallback(async (id: string) => {
    await api.deleteAlert(id);
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const reorderAlerts = useCallback(
    async (orderedIds: string[]) => {
      const previous = alerts;
      const byId = new Map(previous.map((alert) => [alert.id, alert]));
      const requested = orderedIds.flatMap((id) => {
        const alert = byId.get(id);
        if (!alert) return [];
        byId.delete(id);
        return [alert];
      });
      setAlerts([...requested, ...byId.values()]);

      try {
        const response = await api.reorderAlerts(orderedIds);
        setAlerts(response.alerts);
      } catch (e) {
        setAlerts(previous);
        setError(classifyError(e));
        throw e;
      }
    },
    [alerts],
  );

  const addAlert = useCallback<AlertsContextValue["addAlert"]>(
    async (metric, operator, value, delivery, title, body, recurrence) => {
      try {
        await create({
          type: "price",
          condition: { metric, operator, value, recurrence, title, body },
          delivery,
        });
        return true;
      } catch (e) {
        setError(classifyError(e));
        return false;
      }
    },
    [create],
  );

  const addScheduledAlert = useCallback<
    AlertsContextValue["addScheduledAlert"]
  >(
    async (date, title, body, delivery, recurrence, time) => {
      try {
        await create({
          type: "scheduled",
          condition: { type: "scheduled", date, time, recurrence, title, body },
          delivery,
        });
        return true;
      } catch (e) {
        setError(classifyError(e));
        return false;
      }
    },
    [create],
  );

  const editAlert = useCallback<AlertsContextValue["editAlert"]>(
    async (id, metric, operator, value, delivery, title, body, recurrence) => {
      try {
        await update(id, {
          type: "price",
          condition: { metric, operator, value, recurrence, title, body },
          delivery,
        });
        return true;
      } catch (e) {
        setError(classifyError(e));
        return false;
      }
    },
    [update],
  );

  const editScheduledAlert = useCallback<
    AlertsContextValue["editScheduledAlert"]
  >(
    async (id, date, title, body, delivery, recurrence, time) => {
      try {
        await update(id, {
          type: "scheduled",
          condition: { type: "scheduled", date, time, recurrence, title, body },
          delivery,
        });
        return true;
      } catch (e) {
        setError(classifyError(e));
        return false;
      }
    },
    [update],
  );

  const deleteAlert = useCallback(
    async (id: string) => {
      try {
        await remove(id);
      } catch (e) {
        setError(classifyError(e));
      }
    },
    [remove],
  );

  const toggleAlert = useCallback(
    async (id: string, active: boolean) => {
      try {
        await update(id, { active });
      } catch (e) {
        setError(classifyError(e));
      }
    },
    [update],
  );

  const getStatus = useCallback(
    (a: AlertData): "active" | "paused" => (a.active ? "active" : "paused"),
    [],
  );

  const clearError = useCallback(() => setError(null), []);
  const reportError = useCallback((e: unknown) => {
    setError(classifyError(e));
  }, []);

  const alertData = useMemo(
    () =>
      alerts.map((alert) =>
        asAlertData(scheduledAlertForDisplay(alert, timeZone)),
      ),
    [alerts, timeZone],
  );
  const triggeredAlerts = useMemo(
    () => alertData.filter((a) => triggered.has(a.id)),
    [alertData, triggered],
  );

  return useMemo(
    () => ({
      status,
      alerts: alertData,
      triggered,
      triggeredAlerts,
      error,
      reload,
      clearError,
      reportError,
      addAlert,
      addScheduledAlert,
      editAlert,
      editScheduledAlert,
      deleteAlert,
      toggleAlert,
      reorderAlerts,
      getStatus,
      create,
      update,
      remove,
    }),
    [
      status,
      alertData,
      triggered,
      triggeredAlerts,
      error,
      reload,
      clearError,
      reportError,
      addAlert,
      addScheduledAlert,
      editAlert,
      editScheduledAlert,
      deleteAlert,
      toggleAlert,
      reorderAlerts,
      getStatus,
      create,
      update,
      remove,
    ],
  );
}

export function AlertsProvider({ children }: { children: ReactNode }) {
  const value = useAlertsProvider();
  return (
    <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>
  );
}
